import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  FormGroup, FormControlLabel, Checkbox, Box
} from "@mui/material";
import { Delete, Add, Edit } from "@mui/icons-material";
import httpClient from "../../_util/api";

const AVAILABLE_PERMISSIONS = [
  { key: "view_students", label: "View Students" },
  { key: "view_all_applications", label: "View All Applications" },
  { key: "view_reports", label: "View Reports" },
];

const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
function isStrongPassword(password) {
  return STRONG_PASSWORD_REGEX.test(password);
}
function generateStrongPassword() {
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const digit = "23456789";
  const special = "@$!%*?&";
  const all = lower + upper + digit + special;
  let p = "";
  p += lower[Math.floor(Math.random() * lower.length)];
  p += upper[Math.floor(Math.random() * upper.length)];
  p += digit[Math.floor(Math.random() * digit.length)];
  p += special[Math.floor(Math.random() * special.length)];
  for (let i = 0; i < 6; i++) p += all[Math.floor(Math.random() * all.length)];
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

const UserTable = ({ role }) => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const isVendor = role == "vendor";
  const showPermissions = role === "approver" || role === "reviewer";

  const getUserByRole = async () => {
    try {
      const res = await httpClient.get(`/user/role/${role}`);
      if (res.status === 200) {
        setUsers(res.data);
      }
    } catch (error) {
      setError(error.response.data.message);
    }
  };

  useEffect(() => {
    getUserByRole();
  }, [role]);

  const handleOpen = (user = null) => {
    if (user) {
      setEditingUserId(user._id);
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setPassword("");
      setChangePassword(false);
      setPermissions(user.permissions || []);
    } else {
      setPassword(generateStrongPassword());
      setChangePassword(false);
      setPermissions([]);
    }
    setOpen(true);
    setError("");
  };

  const handleClose = () => {
    setOpen(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setChangePassword(false);
    setPermissions([]);
    setEditingUserId(null);
    setError("");
  };

  const handlePermissionToggle = (permKey) => {
    setPermissions((prev) =>
      prev.includes(permKey)
        ? prev.filter((p) => p !== permKey)
        : [...prev, permKey]
    );
  };

  const handleAddUser = async () => {
    if (!firstName || !email || !password) {
      setError("All fields are required");
      return;
    }
    if (!isStrongPassword(password)) {
      setError("Password must be 8+ characters with uppercase, lowercase, number and special character (@$!%*?&)");
      return;
    }
    try {
      const res = await httpClient.post("/user", { firstName, lastName, email, password, role, permissions });
      if (res.status === 201) {
        setUsers([...users, res.data]);
        handleClose();
      }
    } catch (error) {
      setError(error.response.data.message);
    }
  };

  const handleUpdateUser = async () => {
    if (!firstName || !email) {
      setError("First name and email are required");
      return;
    }
    if (changePassword && !password.trim()) {
      setError("Enter a new password or uncheck 'Change password'");
      return;
    }
    if (changePassword && password.trim() && !isStrongPassword(password)) {
      setError("Password must be 8+ characters with uppercase, lowercase, number and special character (@$!%*?&)");
      return;
    }
    try {
      const payload = { firstName, lastName, email, role, permissions };
      if (changePassword && password.trim()) payload.password = password;
      const res = await httpClient.put(`/user/${editingUserId}`, payload);
      if (res.status === 200) {
        setUsers(users.map(user => (user._id === editingUserId ? res.data : user)));
        handleClose();
      }
    } catch (error) {
      setError(error.response.data.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await httpClient.delete(`/user/${userId}`);
      if (res.status === 200) {
        setUsers(users.filter(user => user._id !== userId));
      }
    } catch (error) {
      setError(error.response.data.message);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        Manage {isVendor ? "Partner" : role}s
      </Typography>
      <Paper sx={{ width: "100%", overflow: "hidden", padding: 2, marginTop: "16px" }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#4caf50", color: "white", marginBottom: 2 }}
          startIcon={<Add />}
          onClick={() => handleOpen()}
        >
          Add {isVendor ? "Partner" : role}
        </Button>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#4caf50" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>{isVendor ? "Name" : "First Name"}</TableCell>
                {!isVendor && (
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Last Name</TableCell>
                )}
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Role</TableCell>
                {showPermissions && <TableCell sx={{ color: "white", fontWeight: "bold" }}>Permissions</TableCell>}
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <TableRow key={user._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                     {!isVendor && (
                      <TableCell>{user.lastName}</TableCell>
                    )}
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{isVendor ? "Partner" : user.role}</TableCell>
                    {showPermissions && (
                      <TableCell>
                        {(user.permissions || []).map((p) => {
                          const perm = AVAILABLE_PERMISSIONS.find((ap) => ap.key === p);
                          return perm ? perm.label : p;
                        }).join(", ") || "—"}
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton color="primary" onClick={() => handleOpen(user)}><Edit /></IconButton>
                      <IconButton color="error" onClick={() => handleDeleteUser(user._id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={showPermissions ? (isVendor ? 6 : 7) : (isVendor ? 5 : 6)} align="center">
                    <Typography variant="body1" color="textSecondary">
                      No records found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

          </Table>
        </TableContainer>

        {/* Add/Edit User Modal */}
        <Dialog open={open} onClose={handleClose}>
          {error && (
            <Typography color="error" variant="body" font="bold" sx={{ pt: 3, pl: 3}}>
              {error}
            </Typography>
          )}
          <DialogTitle>{editingUserId ? "Edit User" : "Add New User"}</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label={isVendor ? "Name" : "First Name"}
              type="text"
              fullWidth
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            {!isVendor && (
              <TextField
                margin="dense"
                label="Last Name"
                type="text"
                fullWidth
                variant="outlined"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
             )}
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {!editingUserId ? (
              <TextField
                margin="dense"
                label="Password"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
                required
                error={password.length > 0 && !isStrongPassword(password)}
                helperText={password.length > 0 && !isStrongPassword(password) ? "Use 8+ chars: upper, lower, number, special (@$!%*?&)" : "Auto-filled with a strong password. Edit if needed."}
              />
            ) : (
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={changePassword}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setChangePassword(checked);
                        if (checked) setPassword(generateStrongPassword());
                        else setPassword("");
                      }}
                      sx={{ "&.Mui-checked": { color: "#4caf50" } }}
                    />
                  }
                  label="Change password (optional)"
                />
                {changePassword && (
                  <>
                    <TextField
                      margin="dense"
                      label="New password"
                      type="text"
                      fullWidth
                      variant="outlined"
                      placeholder="Strong password (auto-filled)"
                      onChange={(e) => setPassword(e.target.value)}
                      value={password}
                      error={password.length > 0 && !isStrongPassword(password)}
                      helperText={password.length > 0 && !isStrongPassword(password) ? "Use 8+ chars: upper, lower, number, special (@$!%*?&)" : ""}
                    />
                  </>
                )}
              </Box>
            )}
            {showPermissions && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Permissions
                </Typography>
                <FormGroup>
                  {AVAILABLE_PERMISSIONS.map((perm) => (
                    <FormControlLabel
                      key={perm.key}
                      control={
                        <Checkbox
                          checked={permissions.includes(perm.key)}
                          onChange={() => handlePermissionToggle(perm.key)}
                          sx={{ "&.Mui-checked": { color: "#4caf50" } }}
                        />
                      }
                      label={perm.label}
                    />
                  ))}
                </FormGroup>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#4caf50", color: "white" }}
              onClick={editingUserId ? handleUpdateUser : handleAddUser}
            >
              {editingUserId ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  );
};

export default UserTable;
